import {DtoDetail} from '../../../../../core/shared/dto/dto.detail';
import {VsOwnerTypeEnum} from '../enum/vs-owner-type.enum';
import {VsEncounterSituationEnum} from '../enum/vs-encounter-situation.enum';
import {VsVisitResultEnum} from '../enum/vs-visit-result.enum';
import type {VisitTypeDto} from './visit-type.dto';
import type {CustomerDto} from '../../../../cr/model/dto/entity/customer.dto';
import type {LmLeadDto} from '../../../../lm/model/dto/entity/lm-lead.dto';
import type {AgentDto} from '../../../../../feature-core/am/model/dto/entity/agent.dto';
import type {Address} from '../../../../../feature-core/ad/model/dto/entity/address';
import type {AppointmentDto} from '../../../../as/model/dto/entity/appointment.dto';

/**
 * Entity DTO for {@code vs.Visit}.
 *
 * Represents a field visit by an agent to a customer or lead location.
 * Exactly one of {@code customerDto} / {@code leadDto} must be set (service-guard).
 * The address FK follows the {@code as.Appointment} pattern with an optional
 * free-text fallback via {@code locationText}.
 *
 * Geo capture fields ({@code geoLatitude}, {@code geoLongitude}, {@code geoCapturedAt})
 * are required when the linked {@code visitType.requiresGeo = true}.
 *
 * This entity is also projected into the CH Timeline via the vs→ch mapper
 * ({@code ChVsInteractionMapper}, AP-2.4). Timeline entries are created only
 * for customer visits (not lead-only, per P1 rule 4).
 *
 * Three-field FK pattern is used for all relationships per DTO skill rules.
 * Cross-module DTO types use {@code import type} to prevent circular dependencies.
 *
 * @ticket TM-1000001921
 */
export class VisitDto extends DtoDetail {

    id: number = 0;
    name: string = '';

    // -----------------------------------------------------------------------
    // Visit type
    // -----------------------------------------------------------------------

    /** FK id to vs.VisitType — not null. */
    visitTypeDtoId?: number;
    /** Display name of the visit type. */
    visitTypeDtoName?: string;
    /** Resolved visit type DTO. */
    visitTypeDto?: VisitTypeDto;

    // -----------------------------------------------------------------------
    // Owner — exactly one of customer/lead must be set
    // -----------------------------------------------------------------------

    /** Discriminator: customer (0) or lead (1). */
    ownerType?: VsOwnerTypeEnum;

    /** FK id to cr.Customer — nullable when ownerType=lead. */
    customerDtoId?: number;
    /** Display name of the customer. */
    customerDtoName?: string;
    /** Resolved customer DTO. */
    customerDto?: CustomerDto;

    /** FK id to lm.LmLead — nullable when ownerType=customer. */
    leadDtoId?: number;
    /** Display name of the lead. */
    leadDtoName?: string;
    /** Resolved lead DTO. */
    leadDto?: LmLeadDto;

    // -----------------------------------------------------------------------
    // Agent
    // -----------------------------------------------------------------------

    /** FK id to am.Agent — not null; the agent who conducted the visit. */
    agentDtoId?: number;
    /** Display name of the agent. */
    agentDtoName?: string;
    /** Resolved agent DTO. */
    agentDto?: AgentDto;

    // -----------------------------------------------------------------------
    // Location — FK or free-text fallback (Appointment pattern)
    // -----------------------------------------------------------------------

    /** FK id to ad.Address — preferred location reference. */
    addressDtoId?: number;
    /** Display name of the address. */
    addressDtoName?: string;
    /** Resolved address DTO. */
    addressDto?: Address;

    /**
     * Free-text location description (fallback when no structured address exists,
     * e.g. "Café Rosengarten, Hauptstraße 12").
     */
    locationText?: string;

    // -----------------------------------------------------------------------
    // Appointment link (optional)
    // -----------------------------------------------------------------------

    /** FK id to as.Appointment — nullable; links to a pre-planned appointment. */
    appointmentDtoId?: number;
    /** Display name of the linked appointment. */
    appointmentDtoName?: string;
    /** Resolved appointment DTO. */
    appointmentDto?: AppointmentDto;

    // -----------------------------------------------------------------------
    // Timing
    // -----------------------------------------------------------------------

    /** ISO-8601 planned start time of the visit. */
    plannedStart?: string;

    /** ISO-8601 actual start time (set when the agent arrives on-site). */
    actualStart?: string;

    /** ISO-8601 actual end time (set when the visit concludes). */
    actualEnd?: string;

    // -----------------------------------------------------------------------
    // Geo capture
    // -----------------------------------------------------------------------

    /** Latitude in decimal degrees (WGS 84). Nullable. */
    geoLatitude?: number;

    /** Longitude in decimal degrees (WGS 84). Nullable. */
    geoLongitude?: number;

    /** ISO-8601 timestamp when geo coordinates were captured. Nullable. */
    geoCapturedAt?: string;

    // -----------------------------------------------------------------------
    // Outcome
    // -----------------------------------------------------------------------

    /** Whether the customer/lead was physically encountered. */
    encounterSituation?: VsEncounterSituationEnum;

    /** Outcome of the visit from the agent's perspective. */
    result?: VsVisitResultEnum;

    /** Free-text agent notes about the visit. */
    comment?: string;

}
